import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAcknowledge } from '../../hooks/useAcknowledge';
import Button from '../ui/Button';
import clsx from 'clsx';

interface AcknowledgeModalProps {
  alertId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AcknowledgeModal({
  alertId,
  isOpen,
  onClose,
  onSuccess,
}: AcknowledgeModalProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const acknowledgeMutation = useAcknowledge();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await acknowledgeMutation.mutateAsync({
        alertId,
        note: note.trim() || undefined,
      });
      onSuccess?.();
      onClose();
      setNote('');
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block w-full max-w-md transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('alerts.acknowledgeModal.title')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('alerts.acknowledgeModal.note')}
                  </label>
                  <textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    maxLength={500}
                    className={clsx(
                      'w-full rounded-md border border-gray-300 px-3 py-2',
                      'text-sm text-gray-900 placeholder-gray-400',
                      'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                    )}
                    placeholder={t('alerts.acknowledgeModal.notePlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{note.length}/500</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
              <Button
                type="submit"
                variant="primary"
                disabled={acknowledgeMutation.isPending}
              >
                {acknowledgeMutation.isPending
                  ? t('common.loading')
                  : t('alerts.acknowledgeModal.submit')}
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('alerts.acknowledgeModal.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
